import { Route, Router, Routes } from '@angular/router';
import { canActivateAuthRole } from './core/guards/auth.guard';
import { ErrorComponent } from './components/shared/error/error.component';
import { environment } from '../environments/environment';
import { IRouteConfig } from './core/interfaces/IRouteConfig.interface';
import { routeComponents } from './core/config/routerLink-component-map.config';
import { NotFoundPage } from './components/pages/not-found-page/not-found-page.component';
import { GUARD_REGISTRY } from './helpers/guardRegistry';
import { RoutesConfig } from '../environments/environment.schema';

/**
 * Recursively processes route configuration and generates Angular Routes
 * with role-based guards. Merges parent and child roles for inheritance.
 *
 * @returns Array of Angular Route objects with canActivate guards
 */
function getEnabledRoutes(): Routes {
  const routeConfig: RoutesConfig = environment.routes;
  const routes: Routes = [];

  function processNode(
    node: IRouteConfig,
    key: string,
    parentRoles: string[] = [],
  ) {
    if (!node.enabled) return;

    // Combine parent roles + own roles (unique)
    // add guards by key
    const mergedRoles = Array.from(
      new Set([...(parentRoles ?? []), ...(node.roles ?? [])]),
    );
    const deactivationGuards =
      node.canDeactivateKeys?.map((key) => GUARD_REGISTRY[key]) || [];

    if (node.router_link) {
      if (mergedRoles.length > 0) {
        routes.push({
          path: node.router_link,
          loadComponent: routeComponents[key],
          canActivate: [canActivateAuthRole],
          canDeactivate:
            deactivationGuards.length > 0
              ? deactivationGuards
              : node.canDeactivate,
          data: {
            roles: mergedRoles,
          },
        });
      } else {
        routes.push({
          path: node.router_link,
          loadComponent: routeComponents[key],
        });
      }
    }

    // Process children (object)
    if (node.children && Object.keys(node.children).length > 0) {
      Object.entries(node.children).forEach(([childKey, childNode]) => {
        processNode(childNode, childKey, mergedRoles);
      });
    }
  }

  // Process all top-level nodes
  Object.entries(routeConfig).forEach(([key, node]) => processNode(node, key));

  return routes;
}

const shortcutsRout: Route = {
  path: '',
  pathMatch: 'full',
  redirectTo: 'shortcuts',
};
const errorRout: Route = { path: 'error', component: ErrorComponent };
const NotFoundRout: Route = { path: '**', component: NotFoundPage };

const baseRoutes: Routes = [errorRout];
if (environment.routes.shortcuts.enabled) {
  baseRoutes.push(shortcutsRout);
  baseRoutes.push(NotFoundRout);
}

export function refreshRoutes(router: Router) {
  const newRoutes = [...getEnabledRoutes(), ...baseRoutes];
  router.resetConfig(newRoutes);
}

const enabledMenuRoutes = getEnabledRoutes();
export const routes: Routes = [...enabledMenuRoutes, ...baseRoutes];
